/**
 * Report summary — the executive-summary extract shown ABOVE the compliance sections,
 * so the reviewer instantly knows what type of report they're QA-ing, what asset it
 * covers, and what job is being performed. Written by the compliance worker (see
 * worker/src/compliance/schemas.ts -> ReportSummarySchema); every value is quoted
 * from the report itself, and fields the report doesn't state are simply omitted.
 */
import type { ReportSummary } from '../../reviewer/types';
import { Tile } from '../ui';

interface ReportSummarySectionProps {
  summary: ReportSummary | null;
  checked: boolean;
}

function dwellings(summary: ReportSummary): string | null {
  if (summary.dwellingCount === null && summary.dwellingType === null) return null;
  if (summary.dwellingCount === null) return summary.dwellingType;
  return `${summary.dwellingCount}${summary.dwellingType ? ` ${summary.dwellingType}` : ''}`;
}

export function ReportSummarySection({ summary, checked }: ReportSummarySectionProps) {
  const items: Array<{ label: string; value: string | null }> = summary
    ? [
        { label: 'Report type', value: summary.reportType },
        { label: 'Property', value: summary.propertyDescription },
        { label: 'Dwellings', value: dwellings(summary) },
        { label: 'Land area', value: summary.landArea },
        { label: 'Building area', value: summary.buildingArea },
        { label: 'Average size', value: summary.averageDwellingSize },
        { label: 'Value rate range', value: summary.valueRateRange },
        { label: 'Price range', value: summary.priceRange },
        { label: 'Zoning', value: summary.zoning },
      ].filter((item) => item.value !== null)
    : [];

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Report summary</h2>
      </div>
      <Tile>
        {!summary ? (
          <p className="muted">
            {checked
              ? 'No summary could be extracted for this report.'
              : 'The summary will appear here once the report has been checked.'}
          </p>
        ) : (
          <>
            {items.length > 0 ? (
              <dl className="summary-grid">
                {items.map((item) => (
                  <div className="summary-item" key={item.label}>
                    <dt className="summary-item__label">{item.label}</dt>
                    <dd className="summary-item__value">{item.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {summary.keyFacts.length > 0 ? (
              <ul className="summary-facts">
                {summary.keyFacts.map((fact) => (
                  <li key={fact}>{fact}</li>
                ))}
              </ul>
            ) : null}
            {items.length === 0 && summary.keyFacts.length === 0 ? (
              <p className="muted">The report's opening pages didn't state any summary details.</p>
            ) : null}
          </>
        )}
      </Tile>
    </section>
  );
}
